import React, { useEffect, useRef, useState } from "react";
import '../../../Styles/ScheduleIngestionModal.css'
import LoadingScreen from '../../../ReusableBlocks/LoadingScreen';

/**
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - tenantUrl: string
 * - items: Array<{ id: string, label: string }>
 * - onEditSelection?: () => void
 * - onConfirm: ({ dateStr, timeStr, isoStart }) => void
 * - loading?: boolean  // optional: show spinner on submit
 * - showHealthIndicatorSchedulingWarning?: boolean
 */
export default function ScheduleIngestionModal({
  isOpen,
  onClose,
  tenantUrl = "",
  items = [],
  onEditSelection,
  onConfirm,
  onConfirmRecurring,
  loading = false,
  showHealthIndicatorSchedulingWarning = false,
}) {





  //States
  const overlayRef = useRef(null);
  const cardRef = useRef(null);
  const [dateStr, setDateStr] = useState("");
  const [timeStr, setTimeStr] = useState("");

  const containerRef = useRef(null);
  const oneRef = useRef(null);
  const recRef = useRef(null);
  const [h, setH] = useState(0);

  // 'one-time' | 'recurring'
  const [mode, setMode] = useState("one-time");

  const pad2 = (n) => String(n).padStart(2, "0");


  const getTodayStr = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  };

  // Round current time UP to the next 5-min slot
  const getNext5MinSlot = (d = new Date()) => {
    const copy = new Date(d);
    const m = copy.getMinutes();
    const rounded = Math.ceil(m / 5) * 5;
    if (rounded === 60) {
      copy.setHours(copy.getHours() + 1, 0, 0, 0);
    } else {
      copy.setMinutes(rounded, 0, 0);
    }
    return copy;
  };

  // Convert 12h parts to 24h hour
  const to24h = (h12, ampm) => {
    let h = parseInt(h12, 10);
    if (ampm === "PM" && h < 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    return h;
  };


  const todayLocalDateStr = getTodayStr();
  const [minTodaySlot, setMinTodaySlot] = useState(getNext5MinSlot()); // Date object


  const [hourStr, setHourStr] = useState("");          // "00".."23"
  const [minuteStr, setMinuteStr] = useState("");      // "00","05",...,"55"
  const [ampm, setAmpm] = useState("AM");


  const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
  const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

  const isTodaySelected = dateStr === todayLocalDateStr;


  // Recurring state
  const [frequency, setFrequency] = useState("weeklyDays"); // 'weeklyDays' | 'oncePerWeek'
  const [selectedDays, setSelectedDays] = useState([]);      // e.g. ['MO','WE','FR']
  const [endType, setEndType] = useState("none");            // 'none' | 'byDate'
  const [endDateStr, setEndDateStr] = useState("");




  // Day options (stable keys; avoid duplicate 'T'/'S')
  const DAY_OPTS = [
    { code: "MO", label: "M" },
    { code: "TU", label: "T" },
    { code: "WE", label: "W" },
    { code: "TH", label: "T" },
    { code: "FR", label: "F" },
    { code: "SA", label: "S" },
    { code: "SU", label: "S" },
  ];




  // toggle for weekly-by-days; single-select for once-per-week
  const toggleDay = (code) => {
    if (frequency === "oncePerWeek") {
      setSelectedDays([code]);
      return;
    }
    setSelectedDays((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };




  // put near other state
  const resetFields = () => {
    setDateStr("");
    setHourStr("");
    setMinuteStr("");
    setAmpm("AM");
    setMinTodaySlot(getNext5MinSlot());

    setFrequency("weeklyDays");
    setSelectedDays([]);
    setEndType("none");
    setEndDateStr("");

  };




  // when user switches frequency, normalize selectedDays
  useEffect(() => {
    setSelectedDays((prev) => {
      if (frequency === "oncePerWeek") return prev.slice(0, 1);
      return prev; // keep multi-select
    });
  }, [frequency]);



  // reset every time the modal is opened
  useEffect(() => {
    if (isOpen) {
      resetFields()
      setMode("one-time");
    };
  }, [isOpen]);


 




  // set initial height on open
  useEffect(() => {
    if (!isOpen) return;
    // wait for DOM paint
    const id = requestAnimationFrame(measureActive);
    return () => cancelAnimationFrame(id);
  }, [isOpen]);



  // update height whenever mode changes
  useEffect(() => {
    const id = requestAnimationFrame(measureActive);
    return () => cancelAnimationFrame(id);
  }, [mode]);



  // observe size changes of the active pane
  useEffect(() => {
    if (!window.ResizeObserver) return;
    const target = mode === "one-time" ? oneRef.current : recRef.current;
    if (!target) return;

    const ro = new ResizeObserver(() => measureActive());
    ro.observe(target);
    return () => ro.disconnect();
  }, [mode, isOpen]);



  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    if (isOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);



  useEffect(() => {
    if (!dateStr) return;
    const isToday = dateStr === todayLocalDateStr;

    if (!isToday) return; // no need to clamp for future dates

    const nowSlot = getNext5MinSlot();
    setMinTodaySlot(nowSlot);

    // If user hasn't picked time, auto-pick next slot.
    if (!hourStr || !minuteStr) {
      const h24 = nowSlot.getHours();
      const mm = nowSlot.getMinutes();
      // derive 12h parts
      const ampmNew = h24 >= 12 ? "PM" : "AM";
      let h12 = h24 % 12; if (h12 === 0) h12 = 12;
      setHourStr(pad2(h12));
      setMinuteStr(pad2(mm));
      setAmpm(ampmNew);
      return;
    }

    // If user picked a past time for today, clamp forward to next slot.
    const h24Sel = to24h(hourStr, ampm);
    const selected = new Date();
    const [y, m, d] = dateStr.split("-").map(Number);
    selected.setFullYear(y, m - 1, d);
    selected.setHours(h24Sel, parseInt(minuteStr, 10), 0, 0);

    if (selected < nowSlot) {
      const h24 = nowSlot.getHours();
      const mm = nowSlot.getMinutes();
      const ampmNew = h24 >= 12 ? "PM" : "AM";
      let h12 = h24 % 12; if (h12 === 0) h12 = 12;
      setHourStr(pad2(h12));
      setMinuteStr(pad2(mm));
      setAmpm(ampmNew);
    }
  }, [dateStr]); // runs whenever date changes



  // helper: measure and set height to active pane
  const measureActive = () => {
    const nextH =
      mode === "one-time"
        ? (oneRef.current?.offsetHeight || 0)
        : (recRef.current?.offsetHeight || 0);
    setH(nextH);
  };













  // Close when clicking outside the card
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose?.();
  };




  const handleSubmit = (e) => {

    e.preventDefault();



    if (mode === "recurring") {
      // basic validation
      if (selectedDays.length === 0) {
        alert("Please select at least one day.");
        return;
      }
      if (!hourStr || !minuteStr) {
        alert("Please select a start time.");
        return;
      }
      if (endType === "byDate" && !endDateStr) {
        alert("Please choose an end date.");
        return;
      }

      // convert to 24h
      let hh = parseInt(hourStr, 10);
      const mm = parseInt(minuteStr, 10);
      if (ampm === "PM" && hh < 12) hh += 12;
      if (ampm === "AM" && hh === 12) hh = 0;

      if (mode === 'recurring') {
        const payload = {
          mode: "recurring",
          frequency,                       // 'weeklyDays' | 'oncePerWeek'
          daysOfWeek: selectedDays,        // ['MO','WE',...]
          time: { hour: hh, minute: mm, ampm },
          end: {
            type: endType,                 // 'none' | 'byDate'
            date: endType === "none" ? null : endDateStr // ✅ null if No End
          }
        };

        onConfirmRecurring?.(payload);
        return;
      }
    }





    if (!dateStr || !hourStr || !minuteStr) {
      alert("Please select date and time.");
      return;
    }

    let hh = parseInt(hourStr, 10);
    const mm = parseInt(minuteStr, 10);
    if (ampm === "PM" && hh < 12) hh += 12;
    if (ampm === "AM" && hh === 12) hh = 0;

    const [y, m, d] = dateStr.split("-").map(Number);
    const selected = new Date(y, m - 1, d, hh, mm, 0, 0);
    const now = new Date();

    // 🚫 disallow any moment in the past (covers past dates AND earlier today)
    if (selected < now) {
      alert("Please choose a future date/time.");
      return;
    }

    const assembledTime = `${pad2(hh)}:${pad2(mm)}`;
    const isoStart = selected.toISOString();
    onConfirm?.({ dateStr, timeStr: assembledTime, isoStart });
  };





  // Disable an hour (12h+AM/PM) if its entire hour is in the past today
  const isHourDisabled = (h12Str, ampmVal) => {
    if (!isTodaySelected) return false;
    const h24Opt = to24h(h12Str, ampmVal);
    const minH = minTodaySlot.getHours();
    // If this hour is strictly less than the current min hour, disable
    return h24Opt < minH;
  };

  // Disable a minute option within the currently chosen hour
  const isMinuteDisabled = (mStr) => {
    if (!isTodaySelected) return false;
    if (!hourStr) return false; // don't over-disable minutes until hour chosen
    const h24Opt = to24h(hourStr, ampm);
    const minH = minTodaySlot.getHours();
    const minM = minTodaySlot.getMinutes();
    if (h24Opt > minH) return false;
    if (h24Opt < minH) return true;   // whole hour in the past
    // same hour → minutes must be >= minM
    return parseInt(mStr, 10) < minM;
  };




  if (!isOpen) return null;

  return (
    <>
      {loading && <LoadingScreen />}
      <div
        className="sched-modal__overlay"
        ref={overlayRef}
        onMouseDown={handleOverlayClick}
        aria-hidden="true"
      >
        <div
          className="sched-modal__card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-title"
          ref={cardRef}
        >
          <h2 id="schedule-title" className="sched-modal__title">
            Schedule Ingestion
          </h2>



          {/* Selected Tenant */}
          <div className="sched-modal__block">
            <div className="sched-modal__label">Selected Tenant</div>
            <div className="sched-modal__tenant">
              <span className="sched-modal__tenant-key">Tenant :</span>{" "}
              <span className="sched-modal__tenant-url">{tenantUrl || "-"}</span>
            </div>
          </div>

          {/* Selected Items */}
          <div className="sched-modal__block">
            <div className="sched-modal__row-head">
              <div className="sched-modal__label">Selected Items</div>
              <button
                type="button"
                className="sched-modal__link"
                onClick={onEditSelection}
              >
                Edit Selection
              </button>
            </div>

            <div className="sched-modal__chips">
              {items.length === 0 ? (
                <span className="sched-modal__empty">No items selected</span>
              ) : (
                items.map((it, idx) => (
                  <span key={it.id || idx} className="sched-chip">
                    <span className="sched-chip__index">{idx + 1}.</span>
                    <span className="sched-chip__label">{it.label}</span>
                    {/* <span className="sched-chip__x">×</span> */}
                  </span>
                ))
              )}
            </div>

            {showHealthIndicatorSchedulingWarning && (
              <div className="sched-modal__chi-warning" role="status">
                <span className="sched-modal__chi-warning-icon" aria-hidden="true">⚠</span>
                <span className="sched-modal__chi-warning-text">
                  <strong>Health Indicator Creation</strong> is not supported for scheduled or recurring ingestions and has been removed from this ingestion.
                </span>
              </div>
            )}
          </div>

          {/* Schedule */}
          <div className="sched-modal__block">
            <div className="sched-modal__label">Schedule</div>


            {/* NEW: Segmented tabs */}
            {/* Segmented tabs */}
              <div
                className="seg"
                role="tablist"
                aria-label="Schedule type"
                data-mode={mode} // 'one-time' | 'recurring'
              >
                <span className="seg-glide" aria-hidden="true" />

                <button
                  role="tab"
                  aria-selected={mode === "one-time"}
                  className="seg-btn"
                  type="button"
                  onClick={() => setMode("one-time")}
                >
                  One Time
                </button>


                <button
                  role="tab"
                  aria-selected={mode === "recurring"}
                  className="seg-btn"
                  type="button"
                  onClick={() => setMode("recurring")}
                >
                  Recurring
                </button>

              </div>



            <div className="sched-modal__sub">
              {mode === "one-time"
                ? "Choose date and time when ingestion should run"
                : "Set recurrence (frequency, days, time)."}
            </div>



            {/* Animated height container */}
            <div ref={containerRef} className="mode-anim" style={{ height: h }}>

              {/* ONE-TIME FORM (existing) */}
              {mode === "one-time" && (
                <div
                  ref={oneRef}
                  className={`mode-pane ${mode === "one-time" ? "is-active" : ""}`}
                  aria-hidden={mode !== "one-time"}
                >
                  <form onSubmit={handleSubmit} className="sched-modal__form">
                    <div className="sched-modal__inputs">
                      <label className="sched-input">
                        <span className="sched-input__label">Select Date</span>
                        <div className="sched-input__wrap">
                          <input
                            type="date"
                            className="sched-input__control"
                            value={dateStr}
                            min={todayLocalDateStr}                        // 🚫 blocks selecting older dates in UI
                            onChange={(e) => {
                              const v = e.target.value;
                              // extra clamp in case of manual typing or odd browsers
                              setDateStr(v && v < todayLocalDateStr ? todayLocalDateStr : v);
                            }}
                            required
                          />
                          <span className="sched-input__caret">▾</span>
                        </div>
                      </label>

                      <label className="sched-input">
                        <span className="sched-input__label">Select Time</span>
                        <div className="time-group" role="group" aria-label="Select time">
                          {/* Hour */}
                          <div className="time-seg">
                            {/* Hour */}
                            <select
                              className="time-seg__select"
                              aria-label="Hour"
                              value={hourStr}
                              onChange={(e) => setHourStr(e.target.value)}
                              required
                            >
                              <option value="">HH</option>
                              {HOURS_12.map((h) => (
                                <option
                                  key={h}
                                  value={h}
                                  disabled={isHourDisabled(h, ampm)}
                                >
                                  {h}
                                </option>
                              ))}
                            </select>
                            <span className="time-seg__caret">▾</span>
                          </div>

                          <span className="time-sep">:</span>

                          {/* Minute */}
                          <div className="time-seg">
                            {/* Minute */}
                            <select
                              className="time-seg__select"
                              aria-label="Minute"
                              value={minuteStr}
                              onChange={(e) => setMinuteStr(e.target.value)}
                              required
                            >
                              <option value="">MM</option>
                              {MINUTES.map((m) => (
                                <option
                                  key={m}
                                  value={m}
                                  disabled={isMinuteDisabled(m)}
                                >
                                  {m}
                                </option>
                              ))}
                            </select>
                            <span className="time-seg__caret">▾</span>
                          </div>

                          {/* AM/PM */}
                          <div className="time-seg time-seg--ampm">
                            {/* AM/PM */}
                            <select
                              className="time-seg__select"
                              aria-label="AM or PM"
                              value={ampm}
                              onChange={(e) => setAmpm(e.target.value)}
                              required
                            >
                              <option value="AM" disabled={isTodaySelected && isHourDisabled(hourStr || "12", "AM") && !hourStr}>AM</option>
                              <option value="PM" disabled={false}>PM</option>
                            </select>
                            <span className="time-seg__caret">▾</span>
                          </div>
                        </div>
                      </label>
                    </div>

                    <div className="sched-modal__actions">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? "Scheduling…" : "Schedule Ingestion"}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* RECURRING PLACEHOLDER (UI only for now) */}
              {mode === "recurring" && (
                <div
                  ref={recRef}
                  className={`mode-pane ${mode === "recurring" ? "is-active" : ""}`}
                  aria-hidden={mode !== "recurring"}
                >
                  <form onSubmit={handleSubmit} className="sched-modal__form form-dense">
                    <div className="rec-grid">
                      <label className="sched-input field-freq">
                        <span className="sched-input__label">Frequency</span>
                        <div className="sched-input__wrap">
                          <select
                            id="frequency"
                            name="frequency"
                            className="sched-input__control"
                            value={frequency}
                            onChange={(e) => setFrequency(e.target.value)}>
                            <option value="weeklyDays">Weekly by Days of Week</option>
                            <option value="oncePerWeek">Once per Week</option>
                          </select>

                        </div>
                      </label>

                      <div className="sched-input field-days">

                        <span className="sched-input__label">Days</span>
                        <div className="days-pills days-pills--compact days-pills--nowrap">
                          {DAY_OPTS.map(({ code, label }) => {
                            const active = selectedDays.includes(code);
                            return (
                              <button
                                type="button"
                                key={code}
                                className={`day-pill day-pill--compact ${active ? "is-active" : ""}`}
                                onClick={() => toggleDay(code)}
                                aria-pressed={active}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>

                        {frequency === "oncePerWeek" && (
                          <div className="hint">Select exactly one day.</div>
                        )}
                      </div>

                      <div className="sched-input field-time">
                        <span className="sched-input__label">Start Time</span>
                        <div className="time-group time-group--compact">
                          <div className="time-seg">
                            <select
                              className="time-seg__select time-seg__select--compact"
                              value={hourStr}
                              onChange={(e) => setHourStr(e.target.value)}
                              required
                            >
                              <option value="">HH</option>
                              {HOURS_12.map((h) => (
                                <option key={h} value={h}>{h}</option>
                              ))}
                            </select>
                            <span className="time-seg__caret">▾</span>
                          </div>
                          <span className="time-sep">:</span>
                          <div className="time-seg">
                            <select
                              className="time-seg__select time-seg__select--compact"
                              value={minuteStr}
                              onChange={(e) => setMinuteStr(e.target.value)}
                              required
                            >
                              <option value="">MM</option>
                              {MINUTES.map((m) => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                            <span className="time-seg__caret">▾</span>
                          </div>
                          <div className="time-seg time-seg--ampm">
                            <select
                              className="time-seg__select time-seg__select--compact"
                              value={ampm}
                              onChange={(e) => setAmpm(e.target.value)}
                              required
                            >
                              <option value="AM">AM</option>
                              <option value="PM">PM</option>
                            </select>
                            <span className="time-seg__caret">▾</span>
                          </div>
                        </div>
                      </div>

                      <div className="sched-input field-end">
                        <span className="sched-input__label">End</span>
                        <div className="rec-end rec-end--inline">
                          <label className="rec-end__row rec-end__row--inline">
                            <input
                              type="radio"
                              name="end"
                              value="byDate"
                              checked={endType === "byDate"}
                              onChange={() => setEndType("byDate")}
                            />
                            End by
                            <input
                              type="date"
                              className="sched-input__control rec-end__date rec-end__date--compact"
                              value={endDateStr}
                              onChange={(e) => setEndDateStr(e.target.value)}
                              disabled={endType !== "byDate"}
                              min={todayLocalDateStr}
                            />
                          </label>
                          <label className="rec-end__row rec-end__row--inline">
                            <input
                              type="radio"
                              name="end"
                              value="none"
                              checked={endType === "none"}
                              onChange={() => setEndType("none")}
                            />
                            No End
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="sched-modal__actions">
                      <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading}>
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? "Scheduling…" : "Schedule Ingestion"}
                      </button>
                    </div>
                  </form>
                </div>
              )}

            </div>








          </div>



        </div>
      </div>
    </>
  );
}
